import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Hook para gerenciar municípios favoritos do usuário
 * @param {string} userId - ID do usuário
 * @returns {object} - { favoritos, loading, addFavorito, removeFavorito, isFavorito }
 */
export function useFavoritos(userId) {
  const [favoritos, setFavoritos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carrega favoritos do usuário
  const carregarFavoritos = useCallback(async () => {
    if (!userId) {
      setFavoritos([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('municipios_favoritos')
        .select('*')
        .eq('user_id', userId)
        .order('name_muni');

      if (fetchError) throw fetchError;

      setFavoritos(data || []);
    } catch (err) {
      console.error('Erro ao carregar favoritos:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    carregarFavoritos();
  }, [carregarFavoritos]);

  // Adiciona um município aos favoritos
  const addFavorito = useCallback(async (municipio) => {
    if (!userId) return { success: false, error: 'Usuário não autenticado' };

    try {
      const { data, error } = await supabase
        .from('municipios_favoritos')
        .insert({
          user_id: userId,
          name_muni: municipio.name_muni || municipio,
          code_muni: municipio.code_muni,
          code_state: municipio.code_state
        })
        .select()
        .single();

      if (error) {
        // Se já existe, ignora o erro
        if (error.code === '23505') {
          return { success: true, message: 'Município já está nos favoritos' };
        }
        throw error;
      }

      setFavoritos(prev => [...prev, data].sort((a, b) => a.name_muni.localeCompare(b.name_muni)));
      return { success: true, data };
    } catch (err) {
      console.error('Erro ao adicionar favorito:', err);
      return { success: false, error: err.message };
    }
  }, [userId]);

  // Remove um município dos favoritos
  const removeFavorito = useCallback(async (nameMuni) => {
    if (!userId) return { success: false, error: 'Usuário não autenticado' };

    try {
      const { error } = await supabase
        .from('municipios_favoritos')
        .delete()
        .eq('user_id', userId)
        .eq('name_muni', nameMuni);

      if (error) throw error;

      setFavoritos(prev => prev.filter(f => f.name_muni !== nameMuni));
      return { success: true };
    } catch (err) {
      console.error('Erro ao remover favorito:', err);
      return { success: false, error: err.message };
    }
  }, [userId]);

  // Verifica se um município está nos favoritos
  const isFavorito = useCallback((nameMuni) => {
    return favoritos.some(f => f.name_muni === nameMuni);
  }, [favoritos]);

  return {
    favoritos,
    loading,
    error,
    addFavorito,
    removeFavorito,
    isFavorito,
    recarregar: carregarFavoritos
  };
}
